/**
 * Ryan O'Dowd
 * 2024-08-06
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Keyboard,
  TouchableOpacity,
  View,
} from 'react-native';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  addToSuperSearchHistory,
  getRequestHeaders,
  updateUserSettings,
} from '@utils/store/actions';
import ActivityIndicator from '@components/common/ActivityIndicator';
import Button from '@components/common/Button';
import Icon from '@react-native-vector-icons/material-design-icons';
import {
  KeyboardAvoidingView,
} from 'react-native-keyboard-controller';
import SearchFilterDialog from './SearchFilterDialog';
import SearchHistory from './SearchHistory';
import SearchResult from './SearchResult';
import Searchbar from '@components/common/Searchbar';
import SectionList from '@components/common/SectionList';
import Text from '@components/common/Text';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const SuperSearch = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);
  const headerHeight = hooks.useHeaderHeight();

  const [_searchText, setSearchText] = useState(locationState.q || '');

  const [_isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  const [_searchResults, setSearchResults] = useState({
    publicGoals: [],
    verseTexts: [],
    newUsers: [],
  });
  const [_isFetchingResults, setIsFetchingResults] = useState(false);

  const _escapeInput = (userInput) => {
    return userInput.replace(/[()[\]{}*+^$\\|&]/g, '');
  };

  const goals = hooks.useTypedSelector((state) => state.goals);
  const usersFollowing = hooks.useTypedSelector((state) => state.usersFollowing);
  const userFollowers = hooks.useTypedSelector((state) => state.userFollowers);
  const [_showThreeCharacterError, setShowThreeCharacterErrorTimeout] = useState(false);

  const searchRef = useRef(undefined);

  const userSettings = hooks.useTypedSelector((state) => state.userSettings);

  useEffect(() => {
    if (!_searchText || _searchText.length < 3) {
      setIsFetchingResults(false);
      setSearchResults({
        publicGoals: [],
        verseTexts: [],
        newUsers: [],
      });
      return;
    }

    const _getSearchResults = async (queryString) => {
      setIsFetchingResults(true);
      const translationArg = userSettings.search_verse_texts_preferred_translation_only ? `&translation_id=${userSettings.preferred_translation_id}` : '';
      const response = await fetch(`${globals.apiUrl}/super_search?q=${encodeURIComponent(queryString)}${translationArg}&skip_goals=${+(!userSettings.search_goals_on || !userSettings.search_goals_discover_on)}&skip_new_users=${+(!userSettings.search_friends_on || !userSettings.search_friends_new_friends_on)}&skip_verse_texts=${+!userSettings.search_verse_texts_on}`, {
        headers: await getRequestHeaders(), // @TODO: is this how we do auth for fetch api?
      });
      const responseJson = await response.json();
      if (!responseJson.message) { // @TODO: better error handling (right now an error message is returned on 500)
        setSearchResults({
          publicGoals: responseJson.public_goals,
          verseTexts: responseJson.verse_texts,
          newUsers: responseJson.new_users,
        });
      }
      setIsFetchingResults(false);
    };

    const networkDelay = 200;
    const networkCallTimer = setTimeout(() => {
      _getSearchResults(_searchText);
    }, networkDelay);

    return () => {
      clearTimeout(networkCallTimer);
    };
  }, [_searchText, userSettings.search_goals_on, userSettings.search_goals_discover_on, userSettings.search_friends_on, userSettings.search_friends_new_friends_on, userSettings.search_verse_texts_on, userSettings.preferred_translation_id, userSettings.search_verse_texts_preferred_translation_only]);

  useEffect(() => {
    const delay = 1000;
    setShowThreeCharacterErrorTimeout(false);
    const timer = setTimeout(() => {
      setShowThreeCharacterErrorTimeout(true);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [_searchText]);

  const preferredTranslation = hooks.usePreferredTranslation();

  const lcSearchText = _searchText ? _searchText.toLocaleLowerCase() : '';
  const searchTerms = (lcSearchText ? lcSearchText.split(' ') : []).filter((term) => term).map((term) => term.trim());

  const [_collapsedSections, setCollapsedSections] = useState([]);

  const _renderSearchResultsSectionHeader = (section, title) => { // @TODO: abstract this to a reusable component with size prop
    // @TODO: show filter status in header...quick tap to toggle or open dialog via tune icon
    const isCollapsed = _collapsedSections.includes(section.key);
    title = title.charAt(0).toUpperCase() + title.slice(1);
    let isFetching = false;
    if (_isFetchingResults) {
      if (section.key === 'goals' && userSettings.search_goals_on && userSettings.search_goals_discover_on) {
        isFetching = true;
      } else if (section.key === 'verseTexts') {
        isFetching = true;
      } else if (section.key === 'friends' && userSettings.search_friends_on && userSettings.search_friends_new_friends_on) {
        isFetching = true;
      }
    }

    return (
      <TouchableOpacity
        style={styles.sectionHeaderWrapper}
        onPress={() => setCollapsedSections((curr) => {
          if (isCollapsed) {
            return curr.filter((key) => key !== section.key);
          }
          return [...curr, section.key];
        })}
      >
        <View style={styles.titleAndLoading}>
          <Text style={styles.searchResultsHeaderText}>{title}</Text>
          <ActivityIndicator
            style={styles.isFetchingIcon}
            animating={isFetching}
            hidesWhenStopped={true}
            size={14}
            color={styles.colors.primaryExtraLight}
          />
        </View>
        <Icon
          style={styles.chevronIcon}
          name={isCollapsed ? 'chevron-down' : 'chevron-up'}// @TODO: animata the transition
          size={styles.globalScale * 18}
        />
      </TouchableOpacity>
    );
  };

  const searchResultSections = useMemo(() => {
    const sections = {
      verseTexts: {
        key: 'verseTexts',
        sortOrder: 1,
        title: i18n('verses'),
        data: [],
      },
      goals: {
        key: 'goals',
        sortOrder: 2,
        title: i18n('goals'),
        data: [],
      },
      friends: { // @TODO: this can combine following/followers/find (indicate on item which is which)
        key: 'friends',
        sortOrder: 3,
        title: i18n('friends'),
        data: [],
      },
    }; // @TODO: allow these to be sorted alphabetically, by date, or by last-active
    if (searchTerms.length) { // @TODO: most of the innards here can be memoized
      // @MARK: direct references
      // @TODO: if partial match to book only, show result with book name and chapter rings (keep in mind that "John" should match "John", "1 John", "2 John", and "3 John")
      // @TODO: if partial match to (book + chapter) only, show result with book + chapter (also show verse 1 preview or header text) (if chapter doesn't exist in book, just show book name and chapter rings)
      // @TODO: if partial match to (book + chapter + start verse and/or end verse), show result with book + chapter + verse range for in-context prop (also show verse preview or header text) (colon should be optional...space works the same way) (if chapter or verse/verses don't exist, show book name and chapter rings)
      // @TODO: order matters..."John 2" should NOT match "2 John"

      // @MARK: goals
      if (!_collapsedSections.includes('goals')) { // @TODO: don't hardcode section name
        if (userSettings.search_goals_on) {
          if (_searchText.length >= 3) {
            Object.values(goals).forEach((goal: any) => { // @TODO: differentiate between memory, reading, and group
              /* @TODO: completed goals should be searchable but filtered off by default and/or visually distinct
              const progross = utilities.getGoalProgress(goal.id);
              const isComplete = progross.numCompletedModules === goal.modules.length;

              const goalStatus = isComplete ? 'complete' : 'active';
              */
              if (searchTerms.every((term) => goal.title.toLocaleLowerCase().includes(term))) {
                sections.goals.data.push(goal); // @TODO: don't love hardcoding "goals" here
              }
            });
          }
          _searchResults.publicGoals?.forEach((goal) => {
            if (!sections.goals.data.map((g) => g.id).includes(goal.id)) {
              sections.goals.data.push({...goal, isPublic: true});
            }
          });
          if (!sections.goals.data.length) {
            let qualifier = userSettings.search_goals_my_goals_on ? 'current' : '';
            if (userSettings.search_goals_discover_on) {
              qualifier = `${qualifier}${qualifier ? ' or new' : 'new'}`;
            }
            const actions = [];
            if (!userSettings.search_goals_my_goals_on && _searchText.length >= 3) {
              actions.push(
                <Button
                  key='search_my_goals'
                  mode='contained'
                  theme={styles.colors.accent}
                  onPress={() => dispatch(updateUserSettings({search_goals_my_goals_on: 1}))}
                >
                  Search my goals
                </Button>,
              );
            }
            if (!userSettings.search_goals_discover_on && _searchText.length >= 3) {
              actions.push(
                <Button
                  key='search_for_new_goals'
                  mode='contained'
                  theme={styles.colors.accent}
                  onPress={() => dispatch(updateUserSettings({search_goals_discover_on: 1}))}
                >
                  Search for new goals
                </Button>,
              );
            }
            sections.goals.data.push({
              id: 'no_goals',
              emptyText: _searchText.length < 3 ? 'Please type at least 3 characters to search for goals.' : `No matching ${qualifier} goals.`,
              actions,
            });
          }
        }
      }

      // @MARK: verse texts
      if (!_collapsedSections.includes('verseTexts')) { // @TODO: don't hardcode section name
        if (userSettings.search_verse_texts_on) {
          const matches = utilities.getReferenceMatches(_searchText);
          matches.forEach((match) => {
            sections.verseTexts.data.push({
              id: `${match.book_id}_${match.chapter_number}_${match.verse_number}`,
              isReferencePreview: true,
              translation_id: preferredTranslation.id,
              book_id: match.book_id,
              chapter_number: match.chapter_number,
              start_verse_number: match.start_verse_number,
              end_verse_number: match.end_verse_number,
            });
          });
          if (_searchResults.verseTexts) {
            sections.verseTexts.data.push(..._searchResults.verseTexts);
          }
          if (sections.verseTexts.data.length === 0) {
            const translationQualifier = userSettings.search_verse_texts_preferred_translation_only ? `the ${preferredTranslation.abbreviation}` : 'any available Bible';
            const actions = [];
            if (userSettings.search_verse_texts_preferred_translation_only && _searchText.length >= 3) {
              actions.push(
                <Button
                  key='search_all_versions'
                  mode='contained'
                  theme={styles.colors.accent}
                  onPress={() => dispatch(updateUserSettings({search_verse_texts_preferred_translation_only: 0}))}
                >
                  Search all versions
                </Button>,
              );
            }
            sections.verseTexts.data.push({
              id: 'no_verse_texts',
              emptyText: _searchText.length < 3 ? 'Please type at least 3 characters to search the Bible.' : `No verse matches in ${translationQualifier}.`,
              actions,
            });
          }
        }
      }

      // @MARK: friends/followers
      if (!_collapsedSections.includes('friends')) { // @TODO: don't hardcode section name
        if (userSettings.search_friends_on) {
          if (userSettings.search_friends_my_following_on) {
            usersFollowing.forEach((friend) => {
              if (searchTerms.every((term) => friend.display_name.toLocaleLowerCase().includes(term) || friend.username.toLocaleLowerCase().includes(term))) {
                sections.friends.data.push({...friend, isFollowing: true});
              }
            });
          }
          if (userSettings.search_friends_my_followers_on) {
            userFollowers.forEach((friend) => {
              if (!sections.friends.data.find((f) => f.id === friend.id)) { // if user is already following, no need to check followers // @TODO: this doesn't seem to be working
                if (searchTerms.every((term) => friend.display_name.toLocaleLowerCase().includes(term) || friend.username.toLocaleLowerCase().includes(term))) {
                  sections.friends.data.push({...friend, isFollowing: usersFollowing.find((u) => u.id === friend.id)});
                }
              }
            });
          }
          _searchResults.newUsers?.forEach((user) => {
            if (!sections.friends.data.map((u) => u.id).includes(user.id)) {
              sections.friends.data.push({...user, isFollowing: false});
            }
          });
          // @TODO: filter followers/following
          // @TODO: add find via skeleton and back-end call
          if (!sections.friends.data.length) {
            const actions = [];
            if (!userSettings.search_friends_new_friends_on && _searchText.length >= 3) {
              actions.push(
                <Button
                  key='search_all_users'
                  mode='contained'
                  theme={styles.colors.accent}
                  onPress={() => dispatch(updateUserSettings({search_friends_new_friends_on: 1}))}
                >
                  Search all users
                </Button>,
              );
            }
            sections.friends.data.push({
              id: 'no_friends',
              emptyText: _searchText.length < 3 ? 'Please type at least 3 characters to search for users.' : `No matching ${userSettings.search_friends_new_friends_on ? 'users' : 'friends'}.`,
              actions,
            });
          }
        }
      }
    }
    let filteredSections: any = {};
    if (userSettings.search_goals_on) {
      filteredSections.goals = sections.goals;
    }
    if (userSettings.search_verse_texts_on) {
      filteredSections.verseTexts = sections.verseTexts;
    }
    if (userSettings.search_friends_on) {
      filteredSections.friends = sections.friends;
    }
    filteredSections = Object.values(filteredSections).sort((a: any, b: any) => { // @TODO: maybe limit each section to 5 best matches or something? with a "show more" option for each one?
      return a.sortOrder - b.sortOrder;
    });

    return filteredSections;
  }, [userSettings.search_goals_on, userSettings.search_verse_texts_on, userSettings.search_friends_on, searchTerms, _searchResults, goals, usersFollowing, userFollowers, userSettings.search_friends_my_following_on, userSettings.search_friends_my_followers_on, _searchText, preferredTranslation.abbreviation, userSettings.search_goals_my_goals_on, userSettings.search_goals_discover_on, userSettings.search_verse_texts_preferred_translation_only, dispatch, userSettings.search_friends_new_friends_on, _collapsedSections, preferredTranslation.id, styles.colors.accent, i18n]);

  /* @TODO:
  let keyboardVerticalOffset = utilities.getHeaderHeight();
  if (Platform.OS === 'ios') {
    keyboardVerticalOffset += 16; // @TODO: dynamic, not hardcoded
  } else if (Platform.OS === 'android') {
    keyboardVerticalOffset += 20;
  }
  */

  return (
    <KeyboardAvoidingView
      style={styles.searchbarWrapper}
      behavior='padding'
      keyboardVerticalOffset={headerHeight}
    >
      <Searchbar
        ref={searchRef}
        containerStyles={styles.searchbarContainer}
        style={styles.searchbarInner}
        onChangeText={(searchText) => setSearchText(_escapeInput(searchText))}
        value={_searchText}
        autoCorrect={false}// there are too many names and things that make auto-correct difficult and confusing, especially on blur
        onBlur={() => {
          if (!_searchText) {
            return;
          }

          dispatch(addToSuperSearchHistory(_searchText));
        }}// @TODO: also analytics for tracking if goals, verses, or friends are searched for most often...analytics on search terms too?)
        placeholder={i18n('superSearchPlaceholder')}
        icon='tune'
        onIconPress={() => {
          Keyboard.dismiss();
          setIsFilterDialogOpen((curr) => !curr);
        }}
      />
      <View style={styles.searchResultsContainer}>
        {_searchText
          ? (_searchText.length < 3
            ? (
              <View style={styles.emptySearchResultWrapper}>
                {_showThreeCharacterError
                  ? <Text style={styles.emptySearchResultText}>Please type at least 3 characters to search.</Text>
                  : (
                    <ActivityIndicator
                      style={styles.isFetchingIcon}
                      animating={true}
                      hidesWhenStopped={true}
                      size={14}
                      color={styles.colors.primaryExtraLight}
                    />
                  )
                }
              </View>
            )
            : ((!userSettings.search_goals_on && !userSettings.search_verse_texts_on && !userSettings.search_friends_on)
              ? ( // @TODO: don't like this...if new section is added, have to remember to put it here
                <View style={styles.emptySearchResultWrapper}>
                  <Text style={styles.emptySearchResultText}>{i18n('noFiltersAreTurnedOn')}</Text>
                  <View style={styles.actionsWrapper}>
                    <Button
                      mode='contained'
                      theme={styles.colors.accent}
                      onPress={() => setIsFilterDialogOpen(true)}
                    >
                      Open search settings
                    </Button>
                  </View>
                </View>
              )
              : (
                <SectionList
                  sections={searchResultSections}
                  keyboardShouldPersistTaps='handled'
                  keyboardDismissMode='on-drag'
                  keyExtractor={(item) => {
                    return `${item.id}-${item.verse_id}-${item.goal_type}-${item.username}`;
                  }}
                  renderItem={({item, section}) => {
                    if (item.emptyText) {
                      return (
                        <View
                          key={item.id}
                          style={styles.emptySearchResultWrapper}
                        >
                          <Text style={styles.emptySearchResultText}>{item.emptyText}</Text>
                          {!!item.actions &&
                            <View style={styles.actionsWrapper}>
                              {item.actions}
                            </View>
                          }
                        </View>
                      );
                    }

                    return (
                      <SearchResult
                        key={item.id}
                        item={item}
                        searchText={_searchText}
                        searchTerms={searchTerms}
                        sectionKey={section.key}
                      />
                    );
                  }}
                  renderSectionHeader={({section}) => _renderSearchResultsSectionHeader(section, section.title)}
                />
              )
            )
          )
          : (
            <SearchHistory
              onItemPress={(searchText) => {
                setSearchText(_escapeInput(searchText));
                searchRef.current?.focus();
              }}
            />
          )
        }
      </View>
      <SearchFilterDialog
        isOpen={_isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
      />
    </KeyboardAvoidingView>
  );
};

export default SuperSearch;
